import { useCallback } from 'react'

import {
  applyFilterSelectionUpdate,
  type AlertFilterState,
} from 'features/alerts/features/create-alert/alert-filter-state'
import type { FilterControlProps } from 'features/charts-v2'
import type { SelectedFilterOption } from 'features/charts-v2/types'

type AlertFilterControlProps = Pick<
  FilterControlProps,
  | 'selectedFilterOptions'
  | 'updateFilterSelection'
  | 'getFilterSelection'
  | 'smartSearchValue'
  | 'keywordFilters'
  | 'updateSmartSearch'
  | 'updateKeywordFilters'
>

/** Adapts `AlertDefinition`'s locally-held `AlertFilterState` (lifted to
 * `create-alert/Container.tsx`, since the payload build and the chart
 * preview both need it too) into the `FilterControl` prop bag. Mirrors the
 * shape of `agent-filter-panel-handlers.ts`'s handler-factory pattern, the
 * closest existing precedent for a non-global-store `FilterControl`
 * consumer — see `AlertDefinition`'s doc comment for why nothing in the
 * codebase held state in this exact (bare `SelectedFilterOption[]`) shape
 * already. */
export function useAlertFilterControl(
  filterState: AlertFilterState,
  setFilterState: (next: AlertFilterState) => void,
): AlertFilterControlProps {
  const updateFilterSelection = useCallback<
    AlertFilterControlProps['updateFilterSelection']
  >(
    (field, operator, values, range) => {
      setFilterState({
        ...filterState,
        filters: applyFilterSelectionUpdate(
          filterState.filters,
          field,
          operator,
          values,
          range,
        ),
      })
    },
    [filterState, setFilterState],
  )

  const getFilterSelection = useCallback(
    (field: string): SelectedFilterOption | null =>
      filterState.filters.find((f) => f.field === field) ?? null,
    [filterState.filters],
  )

  const updateSmartSearch = useCallback<
    AlertFilterControlProps['updateSmartSearch']
  >(
    (value) => setFilterState({ ...filterState, smartSearchValue: value }),
    [filterState, setFilterState],
  )

  const updateKeywordFilters = useCallback<
    AlertFilterControlProps['updateKeywordFilters']
  >(
    (filters) => setFilterState({ ...filterState, keywordFilters: filters }),
    [filterState, setFilterState],
  )

  return {
    selectedFilterOptions: filterState.filters,
    updateFilterSelection,
    getFilterSelection,
    smartSearchValue: filterState.smartSearchValue,
    keywordFilters: filterState.keywordFilters,
    updateSmartSearch,
    updateKeywordFilters,
  }
}
